// Vết ghi NHẸ các điểm setState từ listener/provider dùng CHUNG toàn app (TodosProvider/ChatInboxProvider/
// TenantProvider…) — để điều tra React #185 "Maximum update depth exceeded" tái phát ~22 lần trên 6 trang
// khác hẳn nhau (/todos, /attendance, /bookings/new, /pricing, /timeline, /conversations), không tìm ra
// gốc dù đã soát kỹ từng trang (xem memory project_todos_feature.md).
//
// GIẢ THUYẾT MỚI (research 2026-10-04, xem docs/error-logging.md): React 19 đổi cách đếm "nested update"
// — tính CẢ commit còn treo lane Default (không chỉ Sync như React 18). Nhiều listener onSnapshot ĐỘC LẬP
// (todo-counts/unread-chats/planFeatures…), mỗi cái tự setState riêng, nếu dồn ép xảy ra gần nhau trong
// 1 khung thời gian ngắn có thể cộng dồn vượt trần 50 MÀ KHÔNG component nào thực sự "lặp vô hạn" — khớp
// với lý do stack minified luôn chỉ ra sâu trong react-dom (scheduler), không bao giờ ra code app cụ thể.
// Dẫn chứng: facebook/react issue cộng đồng (react-admin #11392) xác nhận đúng cơ chế này cho case tương tự
// (nhiều query độc lập trên danh sách dài).
//
// KHÔNG sửa gì dựa trên giả thuyết chưa xác nhận — chỉ ghi lại NHỊP các listener để lần crash TIẾP THEO
// (app/(app)/error.tsx) đính kèm "ai vừa bắn update trong 2s trước khi vỡ" làm bằng chứng, thay vì đoán.
const RING_SIZE = 60;
const ring: { tag: string; t: number }[] = [];

/** Gọi NGAY TRƯỚC mỗi lần 1 listener dùng chung (onSnapshot toàn-app) sắp setState. Rẻ — chỉ push mảng. */
export function traceUpdate(tag: string): void {
  ring.push({ tag, t: Date.now() });
  if (ring.length > RING_SIZE) ring.shift();
}

/** Đọc lại khi crash — chỉ lấy các mốc trong `windowMs` gần nhất, rút gọn cho vừa `details` log.
 *  2026-10-25: nới 3000→6000ms + RING_SIZE 40→60 — MỌI ca crash gần đây (sau khi đã bọc 7 nguồn qua
 *  queueSharedUpdate) đều cho `updateTrace` RỖNG trong cửa sổ 3s, kể cả khi nguồn thật (vd
 *  `todos-context:myAssignment`, mới phát hiện 2026-10-25) có vòng `getDoc()`/`withNetworkRetry` có
 *  thể mất hơn 3s trên mạng yếu — nới cửa sổ để không bỏ lỡ bằng chứng ở lần crash tiếp theo. */
export function recentUpdateTrace(windowMs = 6000): string[] {
  const cutoff = Date.now() - windowMs;
  return ring.filter(e => e.t >= cutoff).map(e => `${e.tag}@-${Date.now() - e.t}ms`);
}

// Theo dõi tab ẩn/hiện (2026-10-11) — giả thuyết "dồn cục lúc tab hồi từ nền": iOS Safari treo timer +
// kết nối mạng khi tab/PWA bị đưa xuống nền; lúc quay lại, Firestore SDK kết nối lại và giao MỘT CỤC toàn
// bộ thay đổi tích luỹ cho các listener dùng chung gần như cùng lúc → nghi đây là nguồn "dồn cục" gây
// React #185 cho ca Trân (crash lan khắp /todos, /pricing, /conversations — các hook dùng chung chạy mọi
// trang). Ghi lại để `error.tsx` đính kèm "tab vừa ẩn bao lâu trước khi hồi" làm bằng chứng trực tiếp.
let lastVisibilityChangeAt = typeof document !== 'undefined' ? Date.now() : 0;
let lastHiddenDurationMs = 0;
let wasHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const now = Date.now();
    if (document.visibilityState === 'visible' && wasHidden) lastHiddenDurationMs = now - lastVisibilityChangeAt;
    wasHidden = document.visibilityState === 'hidden';
    lastVisibilityChangeAt = now;
  });
}

/** `msSinceVisibilityChange`: bao lâu kể từ lần ẩn/hiện gần nhất. `lastHiddenForMs`: nếu lần gần nhất là
 *  HIỆN trở lại, tab đã ẩn bao lâu trước đó (0 nếu chưa từng ẩn/hiện lần nào, hoặc lần gần nhất là ẨN). */
export function visibilityDiag(): { msSinceVisibilityChange: number; lastHiddenForMs: number } {
  return { msSinceVisibilityChange: Date.now() - lastVisibilityChangeAt, lastHiddenForMs: lastHiddenDurationMs };
}

/** Có đang trong "cửa sổ vừa hồi từ nền" không (dùng để nới cửa sổ gom batch — xem shared-update-batcher.ts). */
export function justResumedFromHidden(graceMs: number): boolean {
  return lastHiddenDurationMs > 0 && Date.now() - lastVisibilityChangeAt < graceMs;
}

// Mốc module vừa được NẠP (2026-10-11, sau khi user tự tái hiện + báo lại): ca thật KHÔNG phải tab hồi
// từ nền (`lastHiddenForMs` luôn rỗng ở mọi lần crash có dữ liệu) — mà là `switchWorkspace()`
// (`lib/auth.tsx`) làm `window.location.href = '/dashboard'`, tức RELOAD TRẦN, nạp lại toàn bộ JS từ
// đầu. Lúc đó 4 hook dùng chung (tenant-context/todo-counts/todos-table/unread-chats) đều mount LẦN ĐẦU
// gần như cùng lúc, mỗi cái tự subscribe Firestore và nhận snapshot ĐẦU TIÊN của mình — đúng khung cảnh
// "nhiều listener độc lập dồn cập nhật" trong giả thuyết gốc (react-admin#11392), chỉ khác là do RELOAD
// chứ không phải do tab resume. Mốc này ghi lại THỜI ĐIỂM module này được nạp — gần đúng thời điểm trang
// vừa tải xong (module nạp ngay khi bundle chạy, trước khi React hydrate xong vài chục ms).
const loadedAt = Date.now();

/** Có đang trong "cửa sổ vừa tải trang" không (hard reload/cold load — bao gồm cả ngay sau switchWorkspace()/
 *  impersonate(), KHÔNG chỉ F5 thường) — dùng để nới cửa sổ gom batch lúc mount hàng loạt listener lần đầu. */
export function justLoaded(graceMs: number): boolean {
  return Date.now() - loadedAt < graceMs;
}
